import { AfterViewInit, Component, HostBinding, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { MessengerService } from '../../services/messenger/messenger.service';
import { IntervalService } from '../../services/interval/interval.service';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { DomSanitizer } from '@angular/platform-browser';
import { MetaService } from '../../services/meta/meta.service';
import { ContentService } from '../../services/content/content.service';
import { TranslateService } from '../../services/translate/translate.service';

@Component({
  selector: 'ibex-img',
  templateUrl: './ibex-image.component.html',
  styleUrls: ['./ibex-image.component.scss'],
  providers: [LocalStorageService]
})
export class IbexImageComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  public static fileApiUrl = 'https://kraken.ibexfusion.com/get/';

  @Input() src: string;
  @Input() itemId: string;
  @Input() size: string;
  @Input() alt: string;
  @Input() auto: boolean;
  @Input() realImage: boolean;
  @Input() forceLive: boolean;
  @Input() forceChange: boolean;
  @Input() saveImage = true;
  @Input() backgroundPosition: string;
  @Input() backgroundSize: string;
  @Input() showLoader = false;
  @Input() showWhenVisible = false;
  @Input() forceShowTitle = false;
  @HostBinding('class.real-image') isRealImage = false;
  notTitleSizes = ['widgetSize', 'oneToOne', 'thumbnail'];
  backgroundImage;
  placeholder: string;
  isContain = false;
  isAuto = false;
  id;
  elements: NodeList;
  showSize: string;
  showFileTitle = false;
  public titles;
  public lang = window['masterLanguage'] || 'de';
  private subscriptions = [];
  private onceVisible;

  constructor(
    private messenger: MessengerService, private localStorageService: LocalStorageService, private sanitizer: DomSanitizer,
    private meta: MetaService, private content: ContentService, private translate: TranslateService
  ) {
    this.lang = this.translate.getCurrentLanguage();
    if (!this.id) {
      this.id = 'ibex-img-' + Date.now().toString(36) + Math.random().toString(36).substr(6);
    }
    this.subscriptions.push(this.meta.$changed.subscribe(changed => {
      this.placeholder = this.meta.getString('images.backgrounds.clear', this.placeholder);
    }));
    if (this.showWhenVisible) {
      IntervalService.set('interval' + this.id, () => {
        if (!this.onceVisible) {
          if (this.isScrolledIntoView()) {
            IntervalService.clear('interval' + this.id);
            this.showImage();
          }
        }
      }, 250);
      this.subscriptions.push(this.messenger.$selectedFilters.subscribe(filter => {
        setTimeout(() => {
          if (!this.onceVisible && filter) {
            if (this.isScrolledIntoView()) {
              this.showImage();
            }
          }
        });
      }));
    } else {
      this.showImage();
    }
  }

  public static getBase64(value: string): string {
    value = btoa(value);
    return value.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  public static convertUrl(url: string, forceLive = false): string {
    if (forceLive) {
      return url;
    }
    if (!!url && url?.startsWith('http') && url.indexOf('tweebie-staging/assets') === -1 && url.indexOf('https://ibexfusion5.s3') === -1) {
      return IbexImageComponent.fileApiUrl + IbexImageComponent.getBase64(url);
    }
    return url;
  }

  isScrolledIntoView() {
    if (this.forceChange) {
      return true;
    }
    const elements = this.getElements();
    if (!!elements) {
      const rect = (<any>elements[0]).getBoundingClientRect();
      const visible = rect.top < window.innerHeight && rect.bottom >= 0 && rect.left < window.innerWidth && rect.right >= 0;
      if (visible) {
        this.onceVisible = true;
      }
      return visible;
    } else {
      this.elements = document.querySelectorAll('.' + this.id);
      return false;
    }
  }

  private init() {
    this.isContain = this.backgroundSize === 'contain'
    this.isAuto = this.auto;
    this.isRealImage = this.realImage;
    if (this.isScrolledIntoView()) {
      this.showImage();
    }
  }

  ngOnInit(): void {
    const item: any = this.content.getById(this.itemId);
    const fileItem = item?.files?.find((f: any) => this.src.includes(f.id));
    this.showFileTitle = this.content.getFile(fileItem?.id)?.detail?.displayFileDescription || fileItem?.displayFileDescription;
    this.init();
  }

  ngOnChanges(): void {
    this.init();
  }

  ngAfterViewInit() {
    this.init();
  }

  ngOnDestroy(): void {
    for (let subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions.length = 0;
  }

  private async showImage() {
    if (!this.src) {
      return setTimeout(() => { this.showImage() }, 50);
    }
    let asset;
    if (this.src?.endsWith('freeForm')) {
      const srcParts = this.src.replace('/freeForm', '').split('/');
      const id = `${srcParts[srcParts.length - 2]}/${srcParts[srcParts.length - 1]}`;
      asset = this.content.getAssetByUrl(id);
      this.titles = asset?.detail?.titles;
    }
    this.showSize = this.size;
    if (this.size && (this.src?.endsWith('freeForm') || /freeForm\?t=\d+$/.test(this.src))) {
      this.showSize = await this.isUrlAvailable(this.src.replace('freeForm', this.size)) ? this.size : 'freeForm';
    }
    let src = this.size && this.src?.endsWith('freeForm') ? this.src.replace('freeForm', this.showSize) : this.src;
    if (asset?.updated !== asset?.created) {
      src += '?t=' + asset.updated.getTime();
    }
    if (this.saveImage) {
      this.localStorageService.getImage(src, this.forceLive).then(url => {
        if (this.realImage) {
          this.backgroundImage = this.sanitizer.bypassSecurityTrustResourceUrl(url || src);
          this.getImageWidth();
          return;
        }
        this.backgroundImage = 'url(' + (url || IbexImageComponent.convertUrl(src, this.forceLive) || '') + ')';
        this.setBackground();
      });
    } else {
      if (this.realImage) {
        this.backgroundImage = this.sanitizer.bypassSecurityTrustResourceUrl(IbexImageComponent.convertUrl(src, this.forceLive));
        this.getImageWidth();
        return;
      }
      this.backgroundImage = 'url(' + IbexImageComponent.convertUrl(src, this.forceLive) + ')';
      this.setBackground();
    }
  }

  async isUrlAvailable(url) {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok; // true if status is 2xx
    } catch (error) {
      return false;
    }
  }

  getImageWidth() {
    const interval = setInterval(() => {
      const image = document.getElementById(this.id) as HTMLImageElement | null;
      if (image?.complete) {
        const titleDiv = document.querySelector<HTMLElement>(`.${this.id}-title`);
        titleDiv && (titleDiv.style.width = `calc(${image.clientWidth}px - 20px)`);
        clearInterval(interval);
      }
    }, 100);
  }

  private setBackground() {
    IntervalService.set('background' + this.id, () => {
      const elements = this.getElements();
      if (!!elements) {
        IntervalService.clear('background' + this.id);
        document.querySelectorAll('.' + this.id).forEach((element: any) => {
          element.style.backgroundImage = this.backgroundImage?.length > 10 ? this.backgroundImage : 'url(' + this.src + ')';
          element.style.backgroundPosition = this.backgroundPosition;
          element.style.backgroundSize = this.backgroundSize;
        });
      }
    }, 10);
  }

  private getElements() {
    if (this.elements?.length) {
      return this.elements;
    } else if (document.querySelectorAll('.' + this.id).length) {
      this.elements = document.querySelectorAll('.' + this.id);
      return this.elements;
    } else {
      return null;
    }
  }
}
